/**
 * Assignment Setup: the business's assignment rules — "when something is assigned to …, do …"
 * (run a workflow for the contact, notify, tag, set a deadline, escalate). Each rule shows what
 * it does, whether it is on, and how its last run went; tap one to edit it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ActivityIndicator, ScrollView, RefreshControl } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { Button, Toggle } from '../components/design-system';
import { StatusBadge } from '../components/lead-finder/leadFinderUi';
import { useRuleOptions, labelOf } from '../components/assignment-rules/useRuleOptions';
import { apiService, AssignmentRule } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useGoBack } from '../hooks/useGoBack';
import { useThemedStyles } from '../hooks/useThemedStyles';
import type { TranslationKey } from '../i18n/en';

export const AssignmentRulesScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const goBack = useGoBack('settings');
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const options = useRuleOptions();

  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await apiService.getAssignmentRules();
      if (res.statusCode === 1000 && res.data) setRules(res.data.items || []);
      else setError(res.errors?.[0]?.message || t('assignmentRules.error.load'));
    } catch (err: any) {
      setError(err?.message || t('assignmentRules.error.load'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (rule: AssignmentRule, isActive: boolean) => {
    setToggling(rule.id);
    try {
      const res = await apiService.setAssignmentRuleActive(rule.id, isActive);
      if (res.statusCode === 1000 && res.data) setRules((list) => list.map((r) => (r.id === rule.id ? { ...r, isActive: res.data!.isActive } : r)));
      else showNotification('error', t('assignmentRules.title'), res.errors?.[0]?.message || t('assignmentRules.error.save'));
    } catch (err: any) {
      showNotification('error', t('assignmentRules.title'), err?.message || t('assignmentRules.error.save'));
    } finally {
      setToggling(null);
    }
  };

  /** "When assigned to …" in words. */
  const whenText = (r: AssignmentRule) => {
    const tr = r.trigger;
    const who = tr.to === 'users'
      ? (tr.userIds || []).map((id) => labelOf(options.users, id)).join(', ')
      : tr.to === 'departments'
        ? (tr.departmentIds || []).map((id) => labelOf(options.departments, id)).join(', ')
        : t('assignmentRules.to.anyone');
    const types = (tr.assignmentTypes || []).map((x) => t(`assignmentRules.type.${x}` as TranslationKey)).join(', ');
    const batch = tr.batch && tr.batch !== 'any' ? t(`assignmentRules.batch.${tr.batch}` as TranslationKey) : '';
    return [t('assignmentRules.when', { who }), types, batch].filter(Boolean).join(' · ');
  };

  /** "Then …" in words. */
  const thenText = (r: AssignmentRule) => {
    const a = r.actions || {};
    const parts: string[] = [];
    if (a.workflow?.enabled) parts.push(t('assignmentRules.summary.workflow', { name: labelOf(options.flows, a.workflow.flowId) }));
    if (a.notify?.enabled) parts.push(t('assignmentRules.summary.notify'));
    if (a.tags?.enabled) parts.push(t('assignmentRules.summary.tags'));
    if (a.deadline?.enabled) parts.push(a.deadline.dueInHours ? t('assignmentRules.summary.due', { hours: a.deadline.dueInHours }) : t('assignmentRules.summary.priority'));
    if (a.escalate?.enabled) parts.push(t('assignmentRules.summary.escalate', { hours: a.escalate.afterHours || 0 }));
    return parts.join(' · ');
  };

  const renderRule = (r: AssignmentRule) => {
    const b = r.lastBatch;
    return (
      <TouchableOpacity key={r.id} style={[styles.card, !r.isActive && styles.cardOff]} activeOpacity={0.7} onPress={() => navigateTo('assignment-rule-edit', r.id)}>
        <View style={[styles.rowBetween, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.flex}>
            <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{r.name}</Text>
            <Text style={[styles.meta, { textAlign: rtl.textStart }]} numberOfLines={2}>{whenText(r)}</Text>
          </View>
          {toggling === r.id ? <ActivityIndicator color={colors.primary} /> : (
            <Toggle value={r.isActive} onValueChange={(v) => toggle(r, v)} size="sm" />
          )}
        </View>
        <View style={[styles.thenRow, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name={rtl.mirrorIcon('arrow-forward') as any} size={14} color={colors.primary as string} />
          <Text style={[styles.then, { textAlign: rtl.textStart }]} numberOfLines={2}>{thenText(r)}</Text>
        </View>
        <View style={[styles.statsRow, { flexDirection: rtl.rowDirection }]}>
          <Text style={styles.meta}>{t('assignmentRules.stats.handled', { count: r.stats?.items || 0 })}</Text>
          {r.stats?.failed ? <StatusBadge label={t('assignmentRules.stats.failed', { count: r.stats.failed })} color={colors.status.error as string} /> : null}
          {b ? (
            <Text style={styles.meta}>
              {t('assignmentRules.stats.last', { done: b.done, total: b.total, when: new Date(b.createdAt).toLocaleString() })}
            </Text>
          ) : null}
        </View>
      </TouchableOpacity>
    );
  };

  const body = () => {
    if (loading) return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
    if (error) {
      return (
        <View style={styles.center}>
          <Ionicons name="alert-circle-outline" size={56} color={colors.error} />
          <Text style={styles.emptyText}>{error}</Text>
          <Button title={t('common.retry')} onPress={() => { setLoading(true); load(); }} variant="primary" />
        </View>
      );
    }
    return (
      <ScrollView
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[colors.primary as string]} />}
      >
        <View style={[styles.intro, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="git-branch-outline" size={20} color={colors.primary as string} />
          <Text style={[styles.introText, { textAlign: rtl.textStart }]}>{t('assignmentRules.intro')}</Text>
        </View>
        {rules.length ? rules.map(renderRule) : (
          <View style={styles.empty}>
            <Ionicons name="people-circle-outline" size={56} color={colors.text.secondary as string} />
            <Text style={styles.emptyTitle}>{t('assignmentRules.empty.title')}</Text>
            <Text style={styles.emptyText}>{t('assignmentRules.empty.message')}</Text>
            <Button title={t('assignmentRules.new')} onPress={() => navigateTo('assignment-rule-edit', 'new')} variant="primary" />
          </View>
        )}
      </ScrollView>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      formHeader={
        <FormHeader
          title={t('assignmentRules.title')}
          subtitle={!loading ? t('assignmentRules.subtitle', { count: rules.length }) : undefined}
          onBack={goBack}
          actions={[{ icon: 'add', primary: true, onPress: () => navigateTo('assignment-rule-edit', 'new'), label: t('assignmentRules.new') }]}
        />
      }
    >
      <View style={styles.container}>{body()}</View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  content: { padding: spacing.md, paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg, gap: spacing.md, maxWidth: 960, width: '100%', alignSelf: 'center' },
  intro: { alignItems: 'flex-start', gap: spacing.sm, padding: spacing.md, borderRadius: designTokens.borderRadius.md, backgroundColor: String(colors.primary) + '10' },
  introText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  card: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, padding: spacing.md, gap: spacing.sm, ...designTokens.shadows.sm },
  cardOff: { opacity: 0.6 },
  cardTitle: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  rowBetween: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  flex: { flex: 1 },
  meta: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  thenRow: { alignItems: 'center', gap: spacing.xs },
  then: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.medium },
  statsRow: { alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm, paddingTop: spacing.xs, borderTopWidth: 1, borderTopColor: colors.border },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xxl, gap: spacing.sm },
  empty: { alignItems: 'center', padding: spacing.xl, gap: spacing.sm },
  emptyTitle: { fontSize: typography.fontSize.lg, color: colors.text.primary, fontFamily: typography.fontFamily.semibold, textAlign: 'center' },
  emptyText: { fontSize: typography.fontSize.base, color: colors.text.secondary, fontFamily: typography.fontFamily.regular, textAlign: 'center', marginBottom: spacing.sm },
});
